/**
 * Notification Send & Preview models - aligned with backend Alaric.Services.Messaging
 *
 * These models are used for:
 * 1. Sending notifications (called by microservices or manual testing)
 * 2. Previewing/rendering notification templates without actually sending
 */

import { DeliveryStatus, NotificationChannel } from "./NotificationEnums";

// ============================================================================
// Send Notification
// ============================================================================

/**
 * Request to send a notification.
 * Maps to: Alaric.Services.Messaging.Dto.SendNotificationRequest
 */
export interface SendNotificationRequest {
    /** Merchant ID */
    merchantId: number;

    /** Event code to send notification for */
    eventCode: string;

    /** Channel to send through */
    channel: NotificationChannel;

    /** Target user ID. Used to lookup device tokens for push notifications. */
    userId?: string;

    /** Recipient email address. Required when Channel = Email. */
    recipientEmail?: string;

    /** Recipient phone number. Required when Channel = Sms. */
    recipientPhone?: string;

    /**
     * Template payload containing template variables (e.g., {"amount": "50.00", "currency": "BGN"}).
     * For push: can optionally include "fcmToken" or "deviceTokens" to bypass DB lookup.
     */
    templatePayload?: Record<string, unknown>;

    /**
     * Data payload dictionary to pass through to push notification data dict.
     * Reserved keys (deliveryId, eventCode, merchantId, deepLink) and keys not in
     * the event definition's AllowedDataPayloadKeys are stripped.
     */
    dataPayload?: Record<string, string>;

    /** ISO language code (e.g., "en", "bg"). Falls back to default if not specified. */
    languageIso?: string;

    /** Optional idempotency key to prevent duplicate sends */
    idempotencyKey?: string;
}

/**
 * Result of sending a notification.
 * Maps to: Alaric.Services.Messaging.Dto.SendNotificationResult
 */
export interface SendNotificationResult {
    /** Unique delivery ID for tracking */
    deliveryId: string;

    /** Current delivery status */
    status: DeliveryStatus;

    /** Provider-specific message ID (e.g., Mailgun ID, FCM message ID) */
    providerMessageId?: string;

    /** Error message if delivery failed */
    errorMessage?: string;

    /** Data payload keys that were provided but not in the event definition's allowlist */
    unresolvedDataPayloadKeys?: string[];
}

// ============================================================================
// Send to Topic
// ============================================================================

/**
 * Request to send a notification to an FCM topic.
 * Maps to: Alaric.Services.Messaging.Dto.SendToTopicRequest
 */
export interface SendToTopicRequest {
    /** Merchant ID */
    merchantId: number;

    /** Topic ID to send to (looked up from DB). Either this or topicName is required. */
    topicId?: string;

    /** FCM topic name to send to directly (for system topics). Either this or topicId is required. */
    topicName?: string;

    /** Event code for template rendering */
    eventCode: string;

    /** Channel (typically Push for topic sends) */
    channel: NotificationChannel;

    /** Template variables to substitute */
    templatePayload?: Record<string, string>;

    /** Data payload for push notification */
    dataPayload?: Record<string, string>;

    /** ISO language code */
    languageIso?: string;
}

/**
 * Result of sending a notification to a topic.
 * Maps to: Alaric.Services.Messaging.Dto.SendToTopicResult
 */
export interface SendToTopicResult {
    deliveryId: string;
    status: DeliveryStatus;
    providerMessageId?: string;
    topicName?: string;
    errorMessage?: string;
}

// ============================================================================
// Preview/Render
// ============================================================================

/**
 * Request to preview/render a notification template without sending.
 * Maps to: Alaric.Services.Messaging.Dto.PreviewRequest
 */
export interface PreviewRequest {
    /** Merchant ID */
    merchantId: number;

    /** Event code */
    eventCode: string;

    /** Channel */
    channel: NotificationChannel;

    /** ISO language code (optional) */
    languageIso?: string;

    /** Language ID (optional, alternative to languageIso) */
    languageId?: string;

    /** Variables to substitute in template placeholders */
    templatePayload?: Record<string, unknown>;

    /** Optional draft subject for previewing unsaved templates */
    draftSubject?: string;

    /** Optional draft title for previewing unsaved templates */
    draftTitle?: string;

    /** Optional draft body for previewing unsaved templates */
    draftBody?: string;

    /** Optional draft deep link for previewing unsaved templates */
    draftDeepLink?: string;
}

/**
 * Result of rendering a notification template.
 * Maps to: Alaric.Services.Messaging.Dto.RenderResult
 */
export interface RenderResult {
    /** Template ID used (if not draft) */
    templateId?: string;

    /** Localization ID used (if not draft) */
    localizationId?: string;

    /** ISO code of the language used for rendering */
    usedLanguageIso: string;

    /** Rendered subject (for Email) */
    renderedSubject?: string;

    /** Rendered title (for Push/Pull) */
    renderedTitle?: string;

    /** Rendered body */
    renderedBody: string;

    /** Rendered deep link URL (after placeholder substitution) */
    renderedDeepLink?: string;

    /** Placeholders in template that weren't provided in payload */
    unresolvedPlaceholders: string[];

    /** Validation warnings (e.g., variable not in allowed list) */
    warnings: string[];
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Create an empty send notification request
 */
export const createEmptySendRequest = (merchantId: number): SendNotificationRequest => ({
    merchantId,
    eventCode: "",
    channel: NotificationChannel.Email,
    userId: "",
    templatePayload: {},
    languageIso: "en",
});

/**
 * Create an empty preview request
 */
export const createEmptyPreviewRequest = (merchantId: number): PreviewRequest => ({
    merchantId,
    eventCode: "",
    channel: NotificationChannel.Email,
    templatePayload: {},
});

/**
 * Check if render result has any issues
 */
export const hasRenderIssues = (result: RenderResult): boolean => {
    return result.unresolvedPlaceholders.length > 0 || result.warnings.length > 0;
};

/**
 * Get delivery status severity for UI display
 */
export const getDeliveryStatusSeverity = (
    status: DeliveryStatus,
): "success" | "info" | "warning" | "danger" => {
    switch (status) {
        case DeliveryStatus.Delivered:
        case DeliveryStatus.Read:
            return "success";
        case DeliveryStatus.Sent:
            return "info";
        case DeliveryStatus.Queued:
            return "warning";
        case DeliveryStatus.Failed:
            return "danger";
        default:
            return "info";
    }
};
